import React from 'react';
import { Box, Typography, Button, TextField, IconButton } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import ParameterValueInput from '../ParameterValueInput';
import { parseTemplateVariablesEnhanced } from '../../utils/executionUtils';

/**
 * InputDataSection Component
 *
 * Section for managing template variable inputs.
 *
 * @param {Object} props
 * @param {Object} props.template - Template object
 * @param {Object} props.executionData - Execution data object
 * @param {Function} props.setExecutionData - Setter for execution data
 * @param {Function} props.handleInputDataChange - Handler for input data changes
 * @param {Function} props.addInputField - Handler for adding input field
 * @param {Function} props.removeInputField - Handler for removing input field
 */
function InputDataSection({
  template,
  executionData,
  setExecutionData,
  handleInputDataChange,
  addInputField,
  removeInputField,
}) {
  const templateVariables = template?.template
    ? parseTemplateVariablesEnhanced(template.template)
    : [];

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Template Variables
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addInputField}
          variant="outlined"
        >
          Add Custom Field
        </Button>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 2, display: 'block' }}
      >
        Variables found in template:{' '}
        {templateVariables.length > 0
          ? templateVariables.map(v => `{{${v}}}`).join(', ')
          : 'No variables found'}
      </Typography>

      {Object.keys(executionData.input_data || {}).length > 0 ? (
        Object.entries(executionData.input_data || {})
          .filter(
            ([key, value]) =>
              key && typeof key === 'string' && value !== undefined
          )
          .map(([key, value]) => {
            const safeKey = String(key || '');
            const safeValue = String(value || '');
            const isTemplateVariable = templateVariables.includes(safeKey);

            return (
              <Box
                key={safeKey}
                sx={{
                  display: 'flex',
                  gap: 1,
                  mb: 2,
                  alignItems: 'center',
                }}
              >
                <TextField
                  label="Parameter Name"
                  value={safeKey || ''}
                  onChange={e => {
                    const newKey = e.target.value;
                    if (newKey && newKey !== safeKey) {
                      const newInputData = { ...executionData.input_data };
                      delete newInputData[safeKey];
                      newInputData[newKey] = safeValue;
                      setExecutionData(prev => ({
                        ...prev,
                        input_data: newInputData,
                      }));
                    }
                  }}
                  size="small"
                  sx={{
                    minWidth: 120,
                    '& .MuiInputBase-root': {
                      backgroundColor: isTemplateVariable
                        ? 'primary.50'
                        : 'background.paper',
                      borderColor: isTemplateVariable
                        ? 'primary.main'
                        : 'divider',
                    },
                  }}
                  InputProps={{
                    readOnly: isTemplateVariable,
                    startAdornment: isTemplateVariable ? (
                      <Box sx={{ mr: 1, color: 'primary.main' }}>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: '0.7rem' }}
                        >
                          {'{{}}'}
                        </Typography>
                      </Box>
                    ) : null,
                  }}
                />
                <ParameterValueInput
                  label={
                    isTemplateVariable ? `Value for {{${safeKey}}}` : 'Value'
                  }
                  value={safeValue || ''}
                  onChange={newValue =>
                    handleInputDataChange(safeKey, newValue)
                  }
                  placeholder={
                    isTemplateVariable
                      ? `Enter value for {{${safeKey}}}...`
                      : 'Enter value...'
                  }
                  required={isTemplateVariable}
                  longTextThreshold={80}
                />
                <IconButton
                  size="small"
                  onClick={() => removeInputField(safeKey)}
                  color="error"
                  disabled={isTemplateVariable}
                  title={
                    isTemplateVariable
                      ? 'Template variables cannot be removed'
                      : 'Remove field'
                  }
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            );
          })
      ) : (
        <Box textAlign="center" py={2}>
          <Typography variant="body2" color="text.secondary">
            {template?.template
              ? 'No template variables found. This template may not require input parameters.'
              : 'Template not loaded yet.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default InputDataSection;
